<script setup>
import FetchCompetitorHistoryDataAction from '@frontJs/components/actions/projectManagement/modules/FetchCompetitorHistoryDataAction.vue';
import ArrowsClockwiseIcon from '@iconify-vue/ph/arrows-clockwise';
import CurrencyEurIcon from '@iconify-vue/ph/currency-eur';
import PencilSimpleIcon from '@iconify-vue/ph/pencil-simple';
import PlusIcon from '@iconify-vue/ph/plus';
import DisplayContentAction from '@js/components/actions/DisplayContentAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import CardGrid from '@js/components/cards/CardGrid.vue';
import MetricCard from '@js/components/cards/MetricCard.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import LinkCell from '@js/components/tables/cells/LinkCell.vue';
import PriceCell from '@js/components/tables/cells/PriceCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { useRoute } from '@js/utils/route';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const { tenant } = defineProps({
    tenant: {
        type: Object,
        required: true,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    count,
    globalFilter,
    filters,
    availableFilters,
    setGlobalFilter,
    setFilters,
    updateData,
    globalData,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'name',
            header: () => 'Nom',
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.name,
                    length: 160,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'url',
            header: () => 'Domaine',
            cell: (props) =>
                h(LinkCell, {
                    text: props.row.original.domain,
                    href: props.row.original.url,
                }),
        }),
        columnHelper.display({
            id: 'seo',
            header: () => 'SEO',
            cell: (props) =>
                props.row.original.seoEnabled &&
                props.row.original.numberOfSeoKeywords > 0
                    ? h(
                          BaseCell,
                          {},
                          {
                              default: () =>
                                  h(ActionButton, {
                                      innerText:
                                          'Voir le rapport' +
                                          ' (' +
                                          props.row.original
                                              .numberOfSeoKeywords +
                                          ')',
                                      href: route(
                                          'front.projectManagement.modules.competitorReport',
                                          {
                                              tenant: tenant.id,
                                              id: props.row.original.id,
                                              tab: 'seo',
                                          },
                                      ),
                                  }),
                          },
                      )
                    : null,
        }),
        columnHelper.display({
            id: 'sea',
            header: () => 'SEA',
            cell: (props) =>
                props.row.original.seaEnabled &&
                props.row.original.numberOfSeaKeywords > 0
                    ? h(
                          BaseCell,
                          {},
                          {
                              default: () =>
                                  h(ActionButton, {
                                      innerText:
                                          'Voir le rapport' +
                                          ' (' +
                                          props.row.original
                                              .numberOfSeaKeywords +
                                          ')',
                                      href: route(
                                          'front.projectManagement.modules.competitorReport',
                                          {
                                              tenant: tenant.id,
                                              id: props.row.original.id,
                                              tab: 'sea',
                                          },
                                      ),
                                  }),
                          },
                      )
                    : null,
        }),
        columnHelper.display({
            id: 'sma',
            header: () => 'SMA',
            cell: (props) =>
                props.row.original.smaMetaUrl
                    ? h(
                          BaseCell,
                          {},
                          {
                              default: () =>
                                  h(ActionButton, {
                                      innerText: 'Voir le rapport',
                                      href: props.row.original.smaMetaUrl,
                                      target: '_blank',
                                  }),
                          },
                      )
                    : null,
        }),
        columnHelper.display({
            id: 'targets',
            header: 'Cibles',
            cell: (innerProps) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () =>
                            h(
                                DisplayContentAction,
                                {
                                    title: 'Cibles',
                                    content: h(
                                        'ul',
                                        { class: 'list-disc' },
                                        innerProps.row.original.competitorTargets.map(
                                            (competitorTarget) =>
                                                h('li', {
                                                    innerText:
                                                        competitorTarget
                                                            .websiteVariant
                                                            .name +
                                                        ' - ' +
                                                        competitorTarget
                                                            .googleWebsite.name,
                                                }),
                                        ),
                                    ),
                                },
                                {
                                    trigger: () =>
                                        h('span', {
                                            class: 'text-blue-500 cursor-pointer hover:underline"',
                                            innerText:
                                                innerProps.row.original
                                                    .competitorTargets.length +
                                                ' cible(s)',
                                        }),
                                },
                            ),
                    },
                ),
        }),
        columnHelper.accessor(() => null, {
            id: 'maxCost',
            header: () => 'Coût max',
            cell: (props) =>
                h(PriceCell, {
                    price: props.row.original.maxCost,
                }),
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'lastFetchedAt',
            header: () => 'Dernière MAJ',
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.lastFetchedAt,
                }),
            enableSorting: false,
        }),
        columnHelper.display({
            id: 'actions',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Actions',
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () => [
                            h(ActionButton, {
                                size: 'small',
                                square: true,
                                title: 'Modifier',
                                href: route(
                                    'front.projectManagement.modules.competitor',
                                    {
                                        tenant: tenant.id,
                                        id: props.row.original.id,
                                    },
                                ),
                                icon: PencilSimpleIcon,
                            }),
                            h(
                                FetchCompetitorHistoryDataAction,
                                {
                                    competitor: props.row.original,
                                    tenant,
                                    callback: updateData,
                                },
                                {
                                    button: ({ action, loading }) =>
                                        h(ActionButton, {
                                            size: 'small',
                                            square: true,
                                            title: 'Récupérer les données',
                                            icon: ArrowsClockwiseIcon,
                                            callback: action,
                                            loading,
                                        }),
                                },
                            ),
                        ],
                    },
                ),
        }),
    ],
    url: route('front.projectManagement.modules.postCompetitors', {
        tenant: tenant.id,
    }),
    columnPinning: {
        right: ['actions'],
    },
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :filters="filters"
        :available-filters="availableFilters"
        :set-filters="setFilters"
        :set-global-filter="setGlobalFilter"
        :loading="loading"
    >
        <template #before-table>
            <CardGrid>
                <MetricCard
                    title="Coût max"
                    :icon="CurrencyEurIcon"
                    type="price"
                    :value="globalData.maxCost"
                />
            </CardGrid>
        </template>

        <template #actions>
            <ActionButton
                size="small"
                :href="
                    route('front.projectManagement.modules.competitor', {
                        tenant: tenant.id,
                    })
                "
                :left-icon="PlusIcon"
            >
                Ajouter
            </ActionButton>
        </template>
    </DataTableLayout>
</template>
